import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MANIFESTO } from "@/constants/testIds";

const chapters = [
  {
    num: "01",
    title: "Family owned. Crafted in-house.",
    body:
      "Welcome to our family-owned jewelry store, where every piece carries a personal touch. Our goldsmith brings generations of skill to every piece — from custom designs to heirloom restorations — so each one feels like it was made just for you.",
    image:
      "https://images.pexels.com/photos/33748058/pexels-photo-33748058.jpeg?auto=compress&cs=tinysrgb&w=1200",
    imageAlt: "Jeweler crafting by hand",
    align: "left",
  },
  {
    num: "02",
    title: "You dream, we create.",
    body:
      "Every piece begins as a whisper — a memory, a proposal, an anniversary. We translate that whisper into wax, then metal, then stone. Nothing leaves the atelier until it is worthy of your name.",
    image:
      "https://images.unsplash.com/photo-1620656798579-1984d9e87df7?auto=format&fit=crop&w=1200&q=80",
    imageAlt: "Fine necklace detail",
    align: "right",
  },
  {
    num: "03",
    title: "Exclusive designs. Exceptional value.",
    body:
      "We remain small on purpose. No middlemen, no showroom mark-ups, no shortcuts — only unhurried craftsmanship at a price that honors both the maker and the wearer.",
    image:
      "https://images.pexels.com/photos/34399145/pexels-photo-34399145.jpeg?auto=compress&cs=tinysrgb&w=1200",
    imageAlt: "Bracelet close-up",
    align: "left",
  },
];

function Chapter({ chapter, i }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  const isRight = chapter.align === "right";
  return (
    <div
      ref={ref}
      className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-center py-24 md:py-40 border-t border-[#1C1C1A]/10"
    >
      <div
        className={`md:col-span-6 ${isRight ? "md:order-2" : ""} space-y-6`}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="font-serif text-[18vw] md:text-[9vw] leading-none text-[#E5D5C1]"
        >
          {chapter.num}
        </motion.div>
        <motion.h3
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="font-serif text-4xl md:text-6xl leading-[1.05] max-w-[520px]"
        >
          {chapter.title}
        </motion.h3>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="font-sans text-base md:text-lg leading-relaxed max-w-md text-[#1C1C1A]/80"
        >
          {chapter.body}
        </motion.p>
      </div>

      <div
        className={`md:col-span-6 ${
          isRight ? "md:order-1" : ""
        } relative overflow-hidden aspect-[4/5] md:aspect-[3/4]`}
      >
        <motion.img
          style={{ y }}
          src={chapter.image}
          alt={chapter.imageAlt}
          className="absolute inset-0 w-full h-[115%] object-cover"
        />
      </div>
    </div>
  );
}

export default function Manifesto() {
  return (
    <section
      id="atelier"
      data-testid={MANIFESTO.section}
      className="relative bg-[#F7F5F2] px-6 md:px-10"
    >
      <div className="pt-24 pb-10 flex items-start justify-between border-b border-[#1C1C1A]/10">
        <span className="text-[10px] uppercase tracking-[0.32em] font-sans text-[#1C1C1A]/70">
          The Atelier — Manifesto
        </span>
        <span className="text-[10px] uppercase tracking-[0.32em] font-sans text-[#1C1C1A]/70">
          Three Chapters
        </span>
      </div>

      {chapters.map((c, i) => (
        <Chapter chapter={c} i={i} key={c.num} />
      ))}
    </section>
  );
}
